import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  createCalendarEvent,
  deleteCalendarEvent,
  fetchCalendarStatus,
  listCalendarEvents,
  oauthStart,
  patchCalendarEvent,
  pushCalendarEvent,
  syncCalendar,
  type CalendarEvent,
} from '../lib/api'
import { resolveAccountAccent } from '../lib/accountAccent'
import { useUiStore } from '../lib/uiStore'
import { Icon } from './Icon'

type CalView = 'month' | 'week' | 'day' | 'year'

const PENDING_KEY = '7gcmail.calendar.pendingAction'
const DOW = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

type PendingAction =
  | { op: 'push'; eventId: string; account_id: string; provider: string }
  | { op: 'delete_push'; eventId: string; account_id: string; provider: string }

/** Target for new events: mail account id, or __local__ for 7GC-only. */
const LOCAL_TARGET = '__local__'

type Draft = {
  id?: string
  title: string
  starts_at: string
  ends_at: string
  day: Date
  /** Required on create — account id or LOCAL_TARGET */
  target: string
}

function startOfDay(d: Date) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function addDays(d: Date, n: number) {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function addMonths(d: Date, n: number) {
  const x = startOfDay(d)
  x.setMonth(x.getMonth() + n, 1)
  return x
}

function addYears(d: Date, n: number) {
  const x = startOfDay(d)
  x.setFullYear(x.getFullYear() + n)
  return x
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function monthGrid(anchor: Date): Date[] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const start = addDays(first, -((first.getDay() + 6) % 7))
  return Array.from({ length: 42 }, (_, i) => addDays(start, i))
}

function weekDays(anchor: Date): Date[] {
  const s = addDays(startOfDay(anchor), -((anchor.getDay() + 6) % 7))
  return Array.from({ length: 7 }, (_, i) => addDays(s, i))
}

function toLocalInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromLocalInput(s: string) {
  return new Date(s)
}

const DEFAULT_DURATION_MS = 60 * 60 * 1000

/** Keep end strictly after start; preserve prior duration when possible. */
function alignEndAfterStart(prevStart: string, prevEnd: string, nextStart: string): string {
  const start = fromLocalInput(nextStart)
  if (Number.isNaN(start.getTime())) return prevEnd
  const oldStart = fromLocalInput(prevStart)
  const oldEnd = fromLocalInput(prevEnd)
  let durationMs = DEFAULT_DURATION_MS
  if (
    !Number.isNaN(oldStart.getTime()) &&
    !Number.isNaN(oldEnd.getTime()) &&
    oldEnd.getTime() > oldStart.getTime()
  ) {
    durationMs = oldEnd.getTime() - oldStart.getTime()
  }
  const end = new Date(start.getTime() + durationMs)
  return toLocalInput(end)
}

/** If user picks an end ≤ start, bump to start + 1h. */
function clampEndAfterStart(startStr: string, endStr: string): string {
  const start = fromLocalInput(startStr)
  const end = fromLocalInput(endStr)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return endStr
  if (end.getTime() > start.getTime()) return endStr
  return toLocalInput(new Date(start.getTime() + DEFAULT_DURATION_MS))
}

function defaultDraftForDay(day: Date, defaultTarget: string): Draft {
  const start = new Date(day)
  start.setHours(10, 0, 0, 0)
  const end = new Date(start)
  end.setHours(11, 0, 0, 0)
  return {
    title: '',
    starts_at: toLocalInput(start),
    ends_at: toLocalInput(end),
    day: startOfDay(day),
    target: defaultTarget,
  }
}

function draftFromEvent(ev: CalendarEvent): Draft {
  return {
    id: ev.id,
    title: ev.title || '',
    starts_at: toLocalInput(new Date(ev.starts_at)),
    ends_at: toLocalInput(new Date(ev.ends_at)),
    day: startOfDay(new Date(ev.starts_at)),
    target: ev.account_id || LOCAL_TARGET,
  }
}

function readPending(): PendingAction | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY)
    if (!raw) return null
    return JSON.parse(raw) as PendingAction
  } catch {
    return null
  }
}

function writePending(p: PendingAction | null) {
  if (!p) sessionStorage.removeItem(PENDING_KEY)
  else sessionStorage.setItem(PENDING_KEY, JSON.stringify(p))
}

function providerFor(provider: string) {
  return provider.includes('gmail') || provider.includes('google') ? 'gmail' : 'microsoft'
}

function eventsOnDay(events: CalendarEvent[], day: Date) {
  return events.filter((e) => sameDay(new Date(e.starts_at), day))
}

export function CalendarPanel({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [view, setView] = useState<CalView>('month')
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()))
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [resumeTried, setResumeTried] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [hiddenAccounts, setHiddenAccounts] = useState<Set<string>>(() => new Set())
  const accountPrefs = useUiStore((s) => s.accountPrefs)

  const range = useMemo(() => {
    if (view === 'day') {
      const s = startOfDay(anchor)
      return { start: s, end: addDays(s, 1) }
    }
    if (view === 'week') {
      const days = weekDays(anchor)
      return { start: days[0], end: addDays(days[6], 1) }
    }
    if (view === 'year') {
      const y = anchor.getFullYear()
      return { start: new Date(y, 0, 1), end: new Date(y + 1, 0, 1) }
    }
    const cells = monthGrid(anchor)
    return { start: cells[0], end: addDays(cells[41], 1) }
  }, [anchor, view])

  const statusQ = useQuery({
    queryKey: ['calendar-status'],
    queryFn: fetchCalendarStatus,
    staleTime: 20_000,
  })

  const eventsQ = useQuery({
    queryKey: ['calendar-events', range.start.toISOString(), range.end.toISOString()],
    queryFn: () =>
      listCalendarEvents({
        start: range.start.toISOString(),
        end: range.end.toISOString(),
      }),
    staleTime: 15_000,
  })

  const events = eventsQ.data?.items || []
  const conflicts = events.filter((e) => e.conflict_state && e.conflict_state !== 'none')
  const accounts = statusQ.data?.accounts || []
  const anyConnected = accounts.some((a) => a.calendar_connected)

  const defaultCreateTarget = useMemo(() => {
    const connected = accounts.find((a) => a.calendar_connected)
    if (connected) return connected.account_id
    if (accounts[0]) return accounts[0].account_id
    return LOCAL_TARGET
  }, [accounts])

  const accountColor = useMemo(() => {
    const m = new Map<string, string>()
    accounts.forEach((a, idx) => {
      m.set(
        a.account_id,
        resolveAccountAccent({
          accountId: a.account_id,
          provider: a.provider,
          index: idx,
          prefColor: accountPrefs[a.account_id]?.color,
        }),
      )
    })
    return m
  }, [accounts, accountPrefs])

  const visibleEvents = useMemo(() => {
    return events.filter((e) => {
      if (!e.account_id) return !hiddenAccounts.has('__local__')
      return !hiddenAccounts.has(e.account_id)
    })
  }, [events, hiddenAccounts])

  function colorForEvent(e: CalendarEvent): string {
    if (!e.account_id) return '#6d28d9'
    return accountColor.get(e.account_id) || providerFallback(e.provider)
  }

  function providerFallback(provider: string | null): string {
    return resolveAccountAccent({ accountId: null, provider, index: 0 })
  }

  function toggleAccount(id: string) {
    setHiddenAccounts((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function connectWrite(accountId: string, provider: string) {
    setBusy(true)
    setNote(null)
    try {
      const res = await oauthStart(providerFor(provider), {
        purpose: 'calendar',
        account_id: accountId,
        write: true,
      })
      window.location.href = res.authorize_url
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'oauth failed')
      setBusy(false)
    }
  }

  async function requestWriteThenRetry(
    accountId: string,
    provider: string,
    pending: PendingAction,
  ) {
    writePending(pending)
    setNote(t('calendar.writeConsent'))
    await connectWrite(accountId, provider)
  }

  useEffect(() => {
    if (resumeTried || statusQ.isLoading || !statusQ.data) return
    const pending = readPending()
    if (!pending) return
    const acct = accounts.find((a) => a.account_id === pending.account_id)
    if (!acct?.calendar_write) return
    setResumeTried(true)
    void (async () => {
      setBusy(true)
      try {
        await pushCalendarEvent(pending.eventId)
        writePending(null)
        await qc.invalidateQueries({ queryKey: ['calendar-events'] })
        await qc.invalidateQueries({ queryKey: ['calendar-status'] })
        setNote(t('calendar.writeDone'))
      } catch (e) {
        setNote(e instanceof Error ? e.message : 'push failed')
      } finally {
        setBusy(false)
      }
    })()
  }, [accounts, qc, resumeTried, statusQ.data, statusQ.isLoading, t])

  async function syncAll() {
    setBusy(true)
    setNote(null)
    try {
      const connected = accounts.filter((x) => x.calendar_connected)
      if (connected.length === 0) {
        setNote(t('calendar.syncNeedConnect'))
        return
      }
      const results = []
      for (const a of connected) {
        results.push(await syncCalendar(a.account_id))
      }
      await qc.invalidateQueries({ queryKey: ['calendar-events'] })
      await qc.invalidateQueries({ queryKey: ['calendar-status'] })
      const failed = results.filter((r) => !r.ok)
      const eventsN = results.reduce((s, r) => s + (r.events || 0), 0)
      if (failed.length) {
        setNote(t('calendar.syncUnavailable'))
      } else {
        setNote(t('calendar.syncedCount', { events: eventsN }))
      }
    } catch {
      setNote(t('calendar.syncUnavailable'))
    } finally {
      setBusy(false)
    }
  }

  function openCreate(day: Date) {
    setAnchor(startOfDay(day))
    setDraft(defaultDraftForDay(day, defaultCreateTarget))
  }

  function openEdit(ev: CalendarEvent) {
    setDraft(draftFromEvent(ev))
  }

  async function saveDraft() {
    if (!draft || !draft.title.trim()) return
    if (!draft.id && !draft.target) {
      setNote(t('calendar.pickCalendar'))
      return
    }
    const starts = fromLocalInput(draft.starts_at)
    const ends = fromLocalInput(draft.ends_at)
    if (Number.isNaN(starts.getTime()) || Number.isNaN(ends.getTime()) || ends <= starts) {
      setNote(t('calendar.invalidRange'))
      return
    }
    setBusy(true)
    setNote(null)
    try {
      if (draft.id) {
        const res = await patchCalendarEvent(draft.id, {
          title: draft.title.trim(),
          starts_at: starts.toISOString(),
          ends_at: ends.toISOString(),
        })
        await qc.invalidateQueries({ queryKey: ['calendar-events'] })
        setDraft(null)
        if (res.needs_write_consent && res.account_id) {
          const a = accounts.find((x) => x.account_id === res.account_id)
          if (a) {
            await requestWriteThenRetry(a.account_id, a.provider, {
              op: 'push',
              eventId: draft.id,
              account_id: a.account_id,
              provider: a.provider,
            })
            return
          }
        }
        setNote(t('calendar.saved'))
      } else {
        const localOnly = draft.target === LOCAL_TARGET
        const acct = localOnly ? null : accounts.find((a) => a.account_id === draft.target) || null
        if (!localOnly && !acct) {
          setNote(t('calendar.pickCalendar'))
          return
        }
        const res = await createCalendarEvent({
          account_id: localOnly ? null : acct!.account_id,
          title: draft.title.trim(),
          starts_at: starts.toISOString(),
          ends_at: ends.toISOString(),
        })
        await qc.invalidateQueries({ queryKey: ['calendar-events'] })
        setDraft(null)
        if (res.needs_write_consent && acct) {
          await requestWriteThenRetry(acct.account_id, acct.provider, {
            op: 'push',
            eventId: res.id,
            account_id: acct.account_id,
            provider: acct.provider,
          })
          return
        }
        setNote(res.local_only || localOnly ? t('calendar.localSaved') : t('calendar.saved'))
      }
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'save failed')
    } finally {
      setBusy(false)
    }
  }

  async function onDelete(evOrId: CalendarEvent | string) {
    const id = typeof evOrId === 'string' ? evOrId : evOrId.id
    setBusy(true)
    setNote(null)
    try {
      const res = await deleteCalendarEvent(id)
      await qc.invalidateQueries({ queryKey: ['calendar-events'] })
      setDraft(null)
      if (res.needs_write_consent && res.account_id) {
        const acct = accounts.find((a) => a.account_id === res.account_id)
        if (acct) {
          await requestWriteThenRetry(acct.account_id, acct.provider, {
            op: 'delete_push',
            eventId: id,
            account_id: acct.account_id,
            provider: acct.provider,
          })
          return
        }
      }
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'delete failed')
    } finally {
      setBusy(false)
    }
  }

  async function resolveConflict(ev: CalendarEvent, mode: 'keep_local' | 'keep_remote') {
    const res = await patchCalendarEvent(ev.id, { resolve_conflict: mode })
    await qc.invalidateQueries({ queryKey: ['calendar-events'] })
    if (res.needs_write_consent && res.account_id) {
      const acct = accounts.find((a) => a.account_id === res.account_id)
      if (acct) {
        await requestWriteThenRetry(acct.account_id, acct.provider, {
          op: 'push',
          eventId: ev.id,
          account_id: acct.account_id,
          provider: acct.provider,
        })
      }
    }
  }

  function shiftAnchor(dir: -1 | 1) {
    if (view === 'year') setAnchor((a) => addYears(a, dir))
    else if (view === 'month') setAnchor((a) => addMonths(a, dir))
    else if (view === 'week') setAnchor((a) => addDays(a, dir * 7))
    else setAnchor((a) => addDays(a, dir))
  }

  const cells = view === 'month' ? monthGrid(anchor) : null
  const week = view === 'week' ? weekDays(anchor) : null
  const dayEvents = view === 'day' ? eventsOnDay(visibleEvents, anchor) : []

  function EventChips({ day, limit = 4 }: { day: Date; limit?: number }) {
    const list = eventsOnDay(visibleEvents, day)
    return (
      <>
        {list.slice(0, limit).map((e) => {
          const color = colorForEvent(e)
          const label = (e.title || t('calendar.untitled')).trim()
          return (
            <button
              key={e.id}
              type="button"
              className={`chip${e.conflict_state !== 'none' ? ' conflict' : ''}${e.local_only ? ' local' : ''}`}
              title={`${label}${e.account_label ? ` · ${e.account_label}` : ''}`}
              style={{
                background: color,
                color: '#fff',
                borderLeft: e.local_only ? '3px solid rgba(255,255,255,0.85)' : undefined,
              }}
              onClick={(ev) => {
                ev.stopPropagation()
                openEdit(e)
              }}
            >
              <span className="chip-t">{label}</span>
            </button>
          )
        })}
        {list.length > limit ? (
          <span className="chip more">+{list.length - limit}</span>
        ) : null}
      </>
    )
  }

  return (
    <div className="rd-cal">
      <div className="rd-panel-head">
        <button type="button" className="rd-back" onClick={onBack} aria-label={t('auth.cancel')}>
          <Icon name="arrow_back" size={20} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2>{t('mail.calendar')}</h2>
          <span className="sub">
            {view === 'day'
              ? anchor.toLocaleDateString(undefined, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              : view === 'year'
                ? String(anchor.getFullYear())
                : anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div className="rd-cal-views" role="group" aria-label={t('calendar.view')}>
          {(['month', 'week', 'day', 'year'] as CalView[]).map((v) => (
            <button
              key={v}
              type="button"
              className={`rd-vt${view === v ? ' sel' : ''}`}
              onClick={() => setView(v)}
            >
              {t(`calendar.view_${v}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="rd-scroll">
        <div className="rd-sec-body rd-cal-body">
          <div className="rd-cal-nav">
            <button type="button" className="rd-rbtn2" onClick={() => shiftAnchor(-1)} aria-label={t('calendar.prev')}>
              <Icon name="arrow_back" size={16} />
            </button>
            <button type="button" className="rd-rbtn2" onClick={() => setAnchor(startOfDay(new Date()))}>
              {t('calendar.today')}
            </button>
            <button type="button" className="rd-rbtn2" onClick={() => shiftAnchor(1)} aria-label={t('calendar.next')}>
              <Icon name="arrow_back" size={16} style={{ transform: 'scaleX(-1)' }} />
            </button>
            <button
              type="button"
              className="rd-rbtn2 rd-cal-add"
              disabled={busy}
              onClick={() => openCreate(anchor)}
            >
              {t('calendar.newEvent')}
            </button>
            {anyConnected ? (
              <button type="button" className="rd-rbtn2" disabled={busy} onClick={() => void syncAll()}>
                {t('calendar.syncNow')}
              </button>
            ) : null}
          </div>

          {!anyConnected ? (
            <p className="d rd-cal-note">{t('calendar.connectInAccountSettings')}</p>
          ) : null}

          {(accounts.length > 0 || visibleEvents.some((e) => !e.account_id)) ? (
            <div className="rd-cal-legend" role="group" aria-label={t('calendar.calendars')}>
              {accounts.map((a) => {
                const color = accountColor.get(a.account_id) || providerFallback(a.provider)
                const checked = !hiddenAccounts.has(a.account_id)
                return (
                  <label key={a.account_id} className={`rd-cal-leg${checked ? '' : ' off'}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAccount(a.account_id)}
                    />
                    <span className="swatch" style={{ background: color }} />
                    <span className="lab">
                      {a.label || a.username}
                      {a.calendar_connected ? '' : ` · ${t('calendar.localOnly')}`}
                    </span>
                  </label>
                )
              })}
              {events.some((e) => !e.account_id) || accounts.length === 0 ? (
                <label className={`rd-cal-leg${!hiddenAccounts.has('__local__') ? '' : ' off'}`}>
                  <input
                    type="checkbox"
                    checked={!hiddenAccounts.has('__local__')}
                    onChange={() => toggleAccount('__local__')}
                  />
                  <span className="swatch" style={{ background: '#6d28d9' }} />
                  <span className="lab">{t('calendar.localOnly')}</span>
                </label>
              ) : null}
            </div>
          ) : null}

          {conflicts.length > 0 ? (
            <div className="rd-card rd-cal-conflict" role="status">
              <strong>{t('calendar.conflicts', { count: conflicts.length })}</strong>
              {conflicts.slice(0, 5).map((ev) => (
                <div key={ev.id} className="rd-cal-conflict-row">
                  <span>{ev.title || t('calendar.untitled')}</span>
                  <button type="button" className="rd-rbtn2" onClick={() => void resolveConflict(ev, 'keep_local')}>
                    {t('calendar.keepLocal')}
                  </button>
                  <button type="button" className="rd-rbtn2" onClick={() => void resolveConflict(ev, 'keep_remote')}>
                    {t('calendar.keepRemote')}
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {note ? <p className="d rd-cal-note" role="status">{note}</p> : null}
          {eventsQ.isLoading ? <p className="d">{t('inbox.loading')}</p> : null}

          <div className="rd-cal-main">
            {view === 'month' && cells ? (
              <div className="rd-cal-month-wrap">
                <div className="rd-cal-dow" aria-hidden>
                  {DOW.map((d) => (
                    <span key={d}>{t(`calendar.dow_${d}`)}</span>
                  ))}
                </div>
                <div className="rd-cal-month" role="grid">
                  {cells.map((day) => {
                    const inMonth = day.getMonth() === anchor.getMonth()
                    return (
                      <div
                        key={day.toISOString()}
                        role="gridcell"
                        className={`rd-cal-cell${inMonth ? '' : ' muted'}${sameDay(day, new Date()) ? ' today' : ''}${sameDay(day, anchor) ? ' sel' : ''}`}
                        onClick={() => openCreate(day)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            openCreate(day)
                          }
                        }}
                        tabIndex={0}
                      >
                        <span className="n">{day.getDate()}</span>
                        <div className="rd-cal-chips">
                          <EventChips day={day} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : null}

            {view === 'week' && week ? (
              <div className="rd-cal-week">
                {week.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={`rd-cal-week-col${sameDay(day, new Date()) ? ' today' : ''}`}
                  >
                    <button
                      type="button"
                      className="rd-cal-week-head"
                      onClick={() => {
                        setAnchor(startOfDay(day))
                        setView('day')
                      }}
                    >
                      <span className="d">
                        {day.toLocaleDateString(undefined, { weekday: 'short' })}
                      </span>
                      <strong>{day.getDate()}</strong>
                    </button>
                    <div
                      className="rd-cal-week-body"
                      onClick={() => openCreate(day)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') openCreate(day)
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <EventChips day={day} limit={12} />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {view === 'day' ? (
              <div className="rd-cal-day">
                <button
                  type="button"
                  className="rd-rbtn2"
                  style={{ marginBottom: 10 }}
                  onClick={() => openCreate(anchor)}
                >
                  {t('calendar.addForDay')}
                </button>
                <ul className="rd-cal-list">
                  {dayEvents.map((e) => (
                    <li key={e.id} className={e.conflict_state !== 'none' ? 'conflict' : ''}>
                      <button type="button" className="rd-cal-list-main" onClick={() => openEdit(e)}>
                        <strong>
                          <span className="swatch" style={{ background: colorForEvent(e) }} />
                          {e.title || t('calendar.untitled')}
                        </strong>
                        <div className="d">
                          {new Date(e.starts_at).toLocaleTimeString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {' – '}
                          {new Date(e.ends_at).toLocaleTimeString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {' · '}
                          {e.local_only ? t('calendar.localOnly') : e.account_label}
                          {e.conflict_state !== 'none' ? ` · ${t('calendar.conflict')}` : ''}
                        </div>
                      </button>
                      <button
                        type="button"
                        className="rd-rbtn2"
                        disabled={busy}
                        aria-label={t('calendar.delete')}
                        onClick={() => void onDelete(e)}
                      >
                        <Icon name="delete" size={16} />
                      </button>
                    </li>
                  ))}
                  {!eventsQ.isLoading && dayEvents.length === 0 ? (
                    <li className="d">{t('calendar.empty')}</li>
                  ) : null}
                </ul>
              </div>
            ) : null}

            {view === 'year' ? (
              <div className="rd-cal-year" role="grid" aria-label={String(anchor.getFullYear())}>
                {Array.from({ length: 12 }, (_, m) => {
                  const monthAnchor = new Date(anchor.getFullYear(), m, 1)
                  const cells = monthGrid(monthAnchor)
                  return (
                    <div key={m} className="rd-cal-year-month">
                      <button
                        type="button"
                        className="rd-cal-year-title"
                        onClick={() => {
                          setAnchor(startOfDay(monthAnchor))
                          setView('month')
                        }}
                      >
                        {monthAnchor.toLocaleDateString(undefined, { month: 'long' })}
                      </button>
                      <div className="rd-cal-year-dow" aria-hidden>
                        {DOW.map((d) => (
                          <span key={d}>{t(`calendar.dow_${d}`).slice(0, 1)}</span>
                        ))}
                      </div>
                      <div className="rd-cal-year-grid">
                        {cells.map((day) => {
                          const inMonth = day.getMonth() === m
                          const n = eventsOnDay(visibleEvents, day).length
                          return (
                            <button
                              key={day.toISOString()}
                              type="button"
                              className={`rd-cal-year-day${inMonth ? '' : ' muted'}${sameDay(day, new Date()) ? ' today' : ''}${n ? ' has' : ''}`}
                              disabled={!inMonth}
                              onClick={() => {
                                setAnchor(startOfDay(day))
                                setView('day')
                              }}
                              title={
                                inMonth
                                  ? `${day.toLocaleDateString()}${n ? ` · ${n}` : ''}`
                                  : undefined
                              }
                            >
                              <span>{day.getDate()}</span>
                              {n > 0 && inMonth ? <i className="dot" aria-hidden /> : null}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {draft ? (
        <div className="rd-cal-modal" role="dialog" aria-modal="true" aria-label={t('calendar.eventDialog')}>
          <div className="rd-cal-modal-card">
            <div className="rd-cal-modal-head">
              <h3>{draft.id ? t('calendar.editEvent') : t('calendar.newEvent')}</h3>
              <button type="button" className="rd-rbtn2" onClick={() => setDraft(null)} aria-label={t('auth.cancel')}>
                <Icon name="close" size={18} />
              </button>
            </div>
            {draft.id ? (
              <p className="d" style={{ marginTop: 0 }}>
                {events.find((e) => e.id === draft.id)?.account_label ||
                  (events.find((e) => e.id === draft.id)?.local_only
                    ? t('calendar.localOnly')
                    : '')}
              </p>
            ) : (
              <label className="rd-cal-field">
                {t('calendar.targetCalendar')} *
                <select
                  value={draft.target}
                  onChange={(e) => setDraft({ ...draft, target: e.target.value })}
                  required
                  aria-required="true"
                >
                  <option value="" disabled>
                    {t('calendar.pickCalendar')}
                  </option>
                  {accounts.map((a) => (
                    <option key={a.account_id} value={a.account_id}>
                      {(a.label || a.username) + (a.username ? ` · ${a.username}` : '')}
                      {a.calendar_connected ? '' : ` · ${t('calendar.localOnly')}`}
                    </option>
                  ))}
                  <option value={LOCAL_TARGET}>{t('calendar.localOnly')}</option>
                </select>
              </label>
            )}
            <label className="rd-cal-field">
              {t('calendar.titlePlaceholder')}
              <input
                autoFocus
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void saveDraft()
                }}
              />
            </label>
            <label className="rd-cal-field">
              {t('calendar.starts')}
              <input
                type="datetime-local"
                value={draft.starts_at}
                onChange={(e) => {
                  const nextStart = e.target.value
                  setDraft({
                    ...draft,
                    starts_at: nextStart,
                    ends_at: alignEndAfterStart(draft.starts_at, draft.ends_at, nextStart),
                  })
                }}
              />
            </label>
            <label className="rd-cal-field">
              {t('calendar.ends')}
              <input
                type="datetime-local"
                value={draft.ends_at}
                min={draft.starts_at}
                onChange={(e) => {
                  const nextEnd = e.target.value
                  setDraft({
                    ...draft,
                    ends_at: clampEndAfterStart(draft.starts_at, nextEnd),
                  })
                }}
              />
              <span className="d" style={{ marginTop: 4 }}>
                {t('calendar.endAfterStartHint')}
              </span>
            </label>
            <div className="rd-cal-modal-actions">
              {draft.id ? (
                <button
                  type="button"
                  className="rd-rbtn2 danger"
                  disabled={busy}
                  onClick={() => void onDelete(draft.id!)}
                >
                  {t('calendar.delete')}
                </button>
              ) : (
                <span />
              )}
              <div className="rd-cal-modal-right">
                <button type="button" className="rd-rbtn2" onClick={() => setDraft(null)}>
                  {t('auth.cancel')}
                </button>
                <button
                  type="button"
                  className="rd-rbtn2 primary"
                  disabled={
                    busy ||
                    !draft.title.trim() ||
                    (!draft.id && !draft.target)
                  }
                  onClick={() => void saveDraft()}
                >
                  {t('calendar.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
